/**
 * Isolated single-definition inspector viewer.
 *
 * Loads ONE block definition in local coordinates (never the whole project
 * model) and renders its nested-block parts as individual meshes so the tree
 * can highlight, hide and isolate real nested blocks.
 */
import * as THREE from "three";
import {
  acceleratedRaycast,
  computeBoundsTree,
  disposeBoundsTree,
} from "three-mesh-bvh";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { ViewCube } from "./ViewCube";
import { MeasurementManager } from "./measurement";

// Accelerated raycasting for click/highlight/measurement (BVH).
THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;
THREE.Mesh.prototype.raycast = acceleratedRaycast;

const HIGHLIGHT = new THREE.Color("#FF2D95");

export class BlockInspectorViewer {
  constructor(container) {
    this.container = container;
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color("#EEF2F8");

    this.camera = new THREE.PerspectiveCamera(45, 1, 0.1, 1e6);
    this.camera.up.set(0, 0, 1);

    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: "high-performance",
    });
    // Cap the pixel ratio: 2x on Retina/4K quadruples the fragment workload
    // for a barely visible gain on a CAD-style viewer.
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    container.appendChild(this.renderer.domElement);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.12;
    // On-demand rendering: any camera movement marks the frame dirty.
    this.controls.addEventListener("change", () => this._markDirty());
    this._dirty = true;

    this.scene.add(new THREE.AmbientLight(0xffffff, 0.75));
    const key = new THREE.DirectionalLight(0xffffff, 1.1);
    key.position.set(1, -1.4, 1.6);
    this.scene.add(key);
    const rim = new THREE.DirectionalLight(0x93c5fd, 0.45);
    rim.position.set(-1.2, 1, -0.8);
    this.scene.add(rim);

    this.group = new THREE.Group();
    this.scene.add(this.group);
    this.meshes = [];
    this.bbox = new THREE.Box3();
    this.opacity = 1;

    this.mode = "orbit";
    this.onSelect = null;
    this.mmPerUnit = 1;
    this.onMeasureChange = null;
    this.onMeasureNotice = null;

    this.raycaster = new THREE.Raycaster();
    this._camTween = null;

    this._onPointerDown = (event) => this._handlePointerDown(event);
    this.renderer.domElement.addEventListener("pointerdown", this._onPointerDown);
    this._onPointerMove = (event) => this._handlePointerMove(event);
    this.renderer.domElement.addEventListener("pointermove", this._onPointerMove);
    this._onPointerUp = (event) => this._handlePointerUp(event);
    window.addEventListener("pointerup", this._onPointerUp);
    this._downPos = null;
    this._onKeyDown = (event) => {
      const tag = event.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (event.key === "Escape") this.measures?.cancelPending();
      if ((event.key === "Delete" || event.key === "Backspace") && this.mode === "measure")
        this.measures?.deleteSelected();
    };
    window.addEventListener("keydown", this._onKeyDown);

    // Interactive section plane: { mode, point, normal, u, v, rect } + gizmo.
    this._section = null;
    this._sectionGizmo = null;
    this._sectionDrag = null;
    this.clipPlane = null;
    this.measures = new MeasurementManager(this);

    this._boundResize = () => this.resize();
    window.addEventListener("resize", this._boundResize);
    this._resizeObserver = new ResizeObserver(() => this.resize());
    this._resizeObserver.observe(container);

    this.viewCube = new ViewCube(container, this.camera, (dir) =>
      this.animateToDirection(dir)
    );

    this.resize();
    this._animate();
  }

  /** Mark the scene dirty — the next animation frame re-renders once. */
  _markDirty() {
    this._dirty = true;
  }

  /** parts: [{ geometry, color, layer, node }] — replaces the current model. */
  setParts(parts) {
    this.clearMeasure();
    this.clearSection();
    this.activeNode = null;
    this.meshes.forEach((mesh) => {
      this.group.remove(mesh);
      mesh.material.dispose();
      // Geometry stays alive: it belongs to the inspector loader's cache,
      // so switching back to an opened panel type is instant.
    });
    this.meshes = [];

    parts.forEach((part, index) => {
      // Phong lighting ≈ Standard here, at a fraction of the shader cost.
      const material = new THREE.MeshPhongMaterial({
        color: new THREE.Color(part.color || "#B0B7C3"),
        shininess: 32,
        specular: 0x222222,
        // DoubleSide: extracted Rhino meshes don't guarantee winding order.
        side: THREE.DoubleSide,
        transparent: this.opacity < 1,
        opacity: this.opacity,
      });
      if (!part.geometry.boundsTree) part.geometry.computeBoundsTree?.();
      const mesh = new THREE.Mesh(part.geometry, material);
      mesh.userData = {
        node: part.node || "",
        layer: part.layer,
        index,
        baseColor: material.color.clone(),
      };
      this.group.add(mesh);
      this.meshes.push(mesh);
    });

    // Local coordinates: centre the definition on its own bounding box.
    this.group.position.set(0, 0, 0);
    this.bbox = new THREE.Box3().setFromObject(this.group);
    if (!this.bbox.isEmpty()) {
      const centre = this.bbox.getCenter(new THREE.Vector3());
      this.group.position.copy(centre.clone().negate());
      this.bbox = new THREE.Box3().setFromObject(this.group);
    }
    // Exploded view vectors: each part slides radially away from the
    // definition centroid, which is the group origin after centring.
    this._explode = 0;
    this.meshes.forEach((mesh) => {
      mesh.position.set(0, 0, 0);
      const box = new THREE.Box3().setFromObject(mesh);
      mesh.userData.explodeDir = box.isEmpty()
        ? new THREE.Vector3()
        : box.getCenter(new THREE.Vector3());
    });
    this.fit(0);
    this._markDirty();
  }

  /**
   * Autodesk-style exploded view. factor 0 = assembled; 1 = every part has
   * doubled its distance from the definition centroid (radial separation).
   */
  setExplode(factor) {
    this._explode = Math.min(Math.max(factor, 0), 1.25);
    this.meshes.forEach((mesh) => {
      const dir = mesh.userData.explodeDir;
      if (dir) mesh.position.copy(dir).multiplyScalar(this._explode);
    });
    this._markDirty();
  }

  /** Overall size in model units (mm handled by the caller). */
  dimensions() {
    if (this.bbox.isEmpty()) return null;
    const size = this.bbox.getSize(new THREE.Vector3());
    return { x: size.x, y: size.y, z: size.z };
  }

  /** Global model transparency (1 = opaque). */
  setOpacity(value) {
    this.opacity = Math.min(Math.max(value, 0.05), 1);
    this.meshes.forEach((mesh) => this._applyMaterialState(mesh));
    this._markDirty();
  }

  /** Colour/opacity of one mesh given the current selection and global opacity. */
  _applyMaterialState(mesh) {
    const node = mesh.userData.node;
    const on =
      this.activeNode &&
      (node === this.activeNode || node.startsWith(`${this.activeNode}/`));
    // Selected element stays fully opaque; everything else ghosts out.
    const ghost = this.activeNode && !on;
    mesh.material.color.copy(on ? HIGHLIGHT : mesh.userData.baseColor);
    mesh.material.transparent = ghost || this.opacity < 1;
    mesh.material.opacity = ghost ? 0.12 : on ? 1 : this.opacity;
    mesh.material.depthWrite = !ghost;
    mesh.material.needsUpdate = true;
  }

  setVisibility(hiddenNodes) {
    const hidden = new Set(hiddenNodes || []);
    this.meshes.forEach((mesh) => {
      const node = mesh.userData.node;
      // A node hides itself and everything nested below it.
      mesh.visible = ![...hidden].some(
        (path) => node === path || node.startsWith(`${path}/`)
      );
    });
    this._markDirty();
  }

  highlight(nodePath) {
    this.activeNode = nodePath || null;
    this.meshes.forEach((mesh) => this._applyMaterialState(mesh));
    this._markDirty();
  }

  fit(duration = 620) {
    if (this.bbox.isEmpty()) return;
    const size = this.bbox.getSize(new THREE.Vector3());
    const radius = Math.max(size.length() / 2, 1);
    const distance = radius / Math.sin((this.camera.fov * Math.PI) / 360);
    this.controls.target.set(0, 0, 0);
    const dir = this.camera.position.clone().normalize();
    const direction = dir.lengthSq() ? dir : new THREE.Vector3(1, -1, 0.7).normalize();
    if (duration === 0) {
      this.camera.position.copy(direction.multiplyScalar(distance * 1.15));
      this.controls.update();
    } else {
      this.animateToDirection(direction, duration, distance * 1.15);
    }
    this.camera.near = Math.max(distance / 1000, 0.01);
    this.camera.far = distance * 100;
    this.camera.updateProjectionMatrix();
    this._markDirty();
  }

  animateToDirection(direction, duration = 620, distanceOverride = null) {
    const target = this.controls.target.clone();
    const offset = this.camera.position.clone().sub(target);
    const distance = distanceOverride || offset.length() || 100;
    const end = direction.clone().normalize();
    if (Math.abs(end.z) > 0.999) end.setY(-0.0025).normalize();
    this._camTween = {
      from: this.camera.position.clone(),
      to: target.clone().add(end.multiplyScalar(distance)),
      start: performance.now(),
      duration,
    };
  }

  /** Named preset views (front/back/left/right/top/bottom/iso). */
  setView(name) {
    const views = {
      front: new THREE.Vector3(0, -1, 0),
      back: new THREE.Vector3(0, 1, 0),
      left: new THREE.Vector3(-1, 0, 0),
      right: new THREE.Vector3(1, 0, 0),
      top: new THREE.Vector3(0, 0, 1),
      bottom: new THREE.Vector3(0, 0, -1),
      iso: new THREE.Vector3(1, -1, 0.8),
    };
    const dir = views[name] || views.iso;
    this.animateToDirection(dir.clone());
  }

  reset() {
    this.clearMeasure();
    this.clearSection();
    this.setExplode(0);
    this.highlight(null);
    this.setVisibility([]);
    this.camera.position.set(1, -1, 0.7);
    this.fit(0);
  }

  setMode(mode) {
    this.mode = mode;
    this.controls.enableRotate = mode !== "pan";
    this.controls.enablePan = true;
    this.measures?.setActive(mode === "measure");
  }

  setMeasureTool(tool) {
    this.measures.setTool(tool);
  }

  deleteSelectedMeasure() {
    this.measures.deleteSelected();
  }

  clearAllMeasures() {
    this.measures.clearAll();
  }

  setMeasureSettings(settings) {
    this.measures.setSettings(settings);
  }

  // ---------- interactive section plane (Autodesk-style) ----------

  /** Creates the section plane for the given axis with its manipulation gizmo. */
  setSectionPlane(mode) {
    const frames = {
      x: { n: [1, 0, 0], u: [0, 1, 0], v: [0, 0, 1] },
      y: { n: [0, 1, 0], u: [0, 0, 1], v: [1, 0, 0] },
      z: { n: [0, 0, 1], u: [1, 0, 0], v: [0, 1, 0] },
    };
    const frame = frames[mode];
    if (!frame) return;
    this.sectionAxis = mode;
    this._section = {
      mode,
      point: new THREE.Vector3(0, 0, 0), // definition is bbox-centred
      normal: new THREE.Vector3(...frame.n),
      u: new THREE.Vector3(...frame.u),
      v: new THREE.Vector3(...frame.v),
    };
    this._buildSectionGizmo();
    this._applySectionClip();
  }

  /** Toolbar slider: slide the plane along its current normal (0..1). */
  setSectionPosition(value) {
    const s = this._section;
    if (!s) return;
    const radius = this.bbox.getSize(new THREE.Vector3()).length() / 2 || 1;
    s.point.set(0, 0, 0).addScaledVector(s.normal, (value * 2 - 1) * radius);
    this._syncSectionGizmo();
    this._updateClipPlane();
  }

  /** Removes the section plane, its gizmo and all clipping. */
  clearSection() {
    this._section = null;
    this.sectionAxis = null;
    this._sectionDrag = null;
    this.controls.enabled = true;
    this._removeSectionGizmo();
    this.clipPlane = null;
    this.renderer.localClippingEnabled = false;
    this.meshes.forEach((mesh) => {
      mesh.material.clippingPlanes = null;
      mesh.material.needsUpdate = true;
    });
    this._markDirty();
  }

  _removeSectionGizmo() {
    if (!this._sectionGizmo) return;
    this.scene.remove(this._sectionGizmo);
    this._sectionGizmo.traverse((child) => {
      child.geometry?.dispose();
      child.material?.dispose();
    });
    this._sectionGizmo = null;
  }

  /** Plane boundary (transparent surface + outline) and the drag handles. */
  _buildSectionGizmo() {
    this._removeSectionGizmo();
    const s = this._section;
    const size = this.bbox.getSize(new THREE.Vector3());
    // Rect spans the model's in-plane extents with an Autodesk-like margin.
    let w = 0;
    let h = 0;
    for (const x of [-size.x / 2, size.x / 2])
      for (const y of [-size.y / 2, size.y / 2])
        for (const z of [-size.z / 2, size.z / 2]) {
          const c = new THREE.Vector3(x, y, z === undefined ? 0 : z);
          w = Math.max(w, Math.abs(c.dot(s.u)));
          h = Math.max(h, Math.abs(c.dot(s.v)));
        }
    w *= 2.6;
    h *= 2.6;
    s.rect = { w, h };

    const gizmo = new THREE.Group();
    const surface = new THREE.Mesh(
      new THREE.PlaneGeometry(w, h),
      new THREE.MeshBasicMaterial({
        color: 0x22d3ee,
        transparent: true,
        opacity: 0.07,
        side: THREE.DoubleSide,
        depthWrite: false,
      })
    );
    gizmo.add(surface);
    const hw = w / 2;
    const hh = h / 2;
    const outline = new THREE.LineLoop(
      new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(-hw, -hh, 0),
        new THREE.Vector3(hw, -hh, 0),
        new THREE.Vector3(hw, hh, 0),
        new THREE.Vector3(-hw, hh, 0),
      ]),
      new THREE.LineBasicMaterial({ color: 0x334155 })
    );
    gizmo.add(outline);

    // Handles: translation arrow along +N, two rotation arcs. Unit-sized on
    // purpose — _animate scales them to a constant screen size at any zoom.
    const handles = new THREE.Group();
    const r = 1;
    const arrowMat = new THREE.MeshBasicMaterial({ color: 0x334155 });
    const shaft = new THREE.Mesh(
      new THREE.CylinderGeometry(r * 0.04, r * 0.04, r * 0.85, 8),
      arrowMat
    );
    shaft.rotation.x = Math.PI / 2;
    shaft.position.z = r * 0.42;
    shaft.userData.handle = "translate";
    const tip = new THREE.Mesh(new THREE.ConeGeometry(r * 0.11, r * 0.3, 16), arrowMat);
    tip.rotation.x = Math.PI / 2;
    tip.position.z = r * 0.95;
    tip.userData.handle = "translate";
    const arcU = new THREE.Mesh(
      new THREE.TorusGeometry(r, r * 0.05, 8, 40, Math.PI / 2),
      new THREE.MeshBasicMaterial({ color: 0xef4444 })
    );
    arcU.rotation.y = -Math.PI / 2; // sweeps +N -> +v, rotates around u
    arcU.userData.handle = "rotU";
    const arcV = new THREE.Mesh(
      new THREE.TorusGeometry(r * 0.8, r * 0.06, 8, 40, Math.PI / 2),
      new THREE.MeshBasicMaterial({ color: 0x3b82f6 })
    );
    arcV.rotation.x = Math.PI / 2; // sweeps +u -> +N, rotates around v
    arcV.userData.handle = "rotV";
    handles.add(shaft, tip, arcU, arcV);
    gizmo.add(handles);
    gizmo.userData.handles = handles;
    this.scene.add(gizmo);
    this._sectionGizmo = gizmo;
    this._syncSectionGizmo();
  }

  _syncSectionGizmo() {
    const s = this._section;
    const gizmo = this._sectionGizmo;
    if (!s || !gizmo) return;
    gizmo.position.copy(s.point);
    gizmo.quaternion.setFromRotationMatrix(
      new THREE.Matrix4().makeBasis(s.u, s.v, s.normal)
    );
    this._markDirty();
  }

  /** Assigns the shared clip plane to every part (shader rebuild once). */
  _applySectionClip() {
    const s = this._section;
    if (!s) return;
    if (!this.clipPlane) this.clipPlane = new THREE.Plane();
    this.renderer.localClippingEnabled = true;
    this.meshes.forEach((mesh) => {
      mesh.material.clippingPlanes = [this.clipPlane];
      mesh.material.needsUpdate = true;
    });
    this._updateClipPlane();
  }

  /** Real-time clip update while dragging: uniforms only, no recompile. */
  _updateClipPlane() {
    const s = this._section;
    if (!s || !this.clipPlane) return;
    // Keep the side the arrow points to: N·(p - P) > 0.
    this.clipPlane.normal.copy(s.normal);
    this.clipPlane.constant = -s.normal.dot(s.point);
    this._markDirty();
  }

  _gizmoHandleAt(event) {
    if (!this._sectionGizmo) return null;
    const rect = this.renderer.domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );
    this.raycaster.setFromCamera(ndc, this.camera);
    const hits = this.raycaster.intersectObjects(
      this._sectionGizmo.userData.handles.children,
      false
    );
    return hits[0] || null;
  }

  _handlePointerMove(event) {
    if (this.mode === "measure" && !this._sectionDrag) {
      this.measures.handleMove(this._ndc(event));
      return;
    }
    const drag = this._sectionDrag;
    const s = this._section;
    if (!drag || !s) return;
    const rect = this.renderer.domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );
    this.raycaster.setFromCamera(ndc, this.camera);
    const ray = this.raycaster.ray;
    const radius = this.bbox.getSize(new THREE.Vector3()).length() / 2 || 1;

    if (drag.kind === "translate") {
      // Closest point of the normal line (P0 + s·N) to the pointer ray.
      const w0 = ray.origin.clone().sub(drag.startPoint);
      const b = s.normal.dot(ray.direction);
      const dPrime = w0.dot(s.normal);
      const ePrime = w0.dot(ray.direction);
      const denom = 1 - b * b;
      if (Math.abs(denom) < 1e-6) return;
      const t = (dPrime * b - ePrime) / denom;
      const sParam = Math.min(Math.max(dPrime + t * b, -radius * 2.5), radius * 2.5);
      s.point.copy(drag.startPoint).addScaledVector(s.normal, sParam);
    } else {
      // Rotate the start frame around the captured in-plane axis.
      const axis = drag.kind === "rotU" ? drag.startU : drag.startV;
      const plane = new THREE.Plane(axis.clone(), -axis.dot(drag.startPoint));
      const hit = new THREE.Vector3();
      if (!ray.intersectPlane(plane, hit)) return;
      const v0 = drag.startHit.clone().sub(drag.startPoint);
      const v1 = hit.clone().sub(drag.startPoint);
      const sin = axis.dot(v0.clone().cross(v1));
      const cos = v0.dot(v1);
      const q = new THREE.Quaternion().setFromAxisAngle(axis, Math.atan2(sin, cos));
      s.normal.copy(drag.startNormal).applyQuaternion(q);
      s.u.copy(drag.startU).applyQuaternion(q);
      s.v.copy(drag.startV).applyQuaternion(q);
    }
    this._syncSectionGizmo();
    this._updateClipPlane();
  }

  _handlePointerUp(event) {
    if (this._sectionDrag) {
      this._sectionDrag = null;
      this.controls.enabled = true;
      this._downPos = null;
      return;
    }
    if (this.mode === "measure" && this._downPos && event?.button === 0) {
      const moved =
        Math.abs(event.clientX - this._downPos.x) +
        Math.abs(event.clientY - this._downPos.y);
      if (moved <= 4) this.measures.handleClick(this._ndc(event));
    }
    this._downPos = null;
  }

  /**
   * Autodesk-style section box: six clipping planes forming a box scaled
   * around the (centred) definition bbox. scale 1 = full model, 0.1 = tight.
   */
  setSectionBox(scale = 0.6) {
    this._section = null;
    this._removeSectionGizmo();
    const s = Math.min(Math.max(scale, 0.02), 1);
    const size = this.bbox.getSize(new THREE.Vector3());
    const ex = (size.x / 2) * s;
    const ey = (size.y / 2) * s;
    const ez = (size.z / 2) * s;
    this.sectionAxis = "box";
    const planes = [
      new THREE.Plane(new THREE.Vector3(1, 0, 0), ex),
      new THREE.Plane(new THREE.Vector3(-1, 0, 0), ex),
      new THREE.Plane(new THREE.Vector3(0, 1, 0), ey),
      new THREE.Plane(new THREE.Vector3(0, -1, 0), ey),
      new THREE.Plane(new THREE.Vector3(0, 0, 1), ez),
      new THREE.Plane(new THREE.Vector3(0, 0, -1), ez),
    ];
    this.renderer.localClippingEnabled = true;
    this.meshes.forEach((mesh) => {
      mesh.material.clippingPlanes = planes;
      mesh.material.needsUpdate = true;
    });
    this._markDirty();
  }

  // ---------- measurement ----------
  clearMeasure() {
    this.measures?.clearAll();
  }

  _ndc(event) {
    const rect = this.renderer.domElement.getBoundingClientRect();
    return new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );
  }

  /** Visible-mesh hits, dropping points on the clipped-away side of a section. */
  pickHits(ndc) {
    this.raycaster.setFromCamera(ndc, this.camera);
    const hits = this.raycaster.intersectObjects(
      this.meshes.filter((mesh) => mesh.visible),
      false
    );
    return hits.filter((hit) => {
      const planes = hit.object.material.clippingPlanes;
      if (!planes || !planes.length) return true;
      return planes.every((plane) => plane.distanceToPoint(hit.point) >= -1e-9);
    });
  }

  /** Visible meshes the laser probes may raycast against. */
  probeTargets() {
    return this.meshes.filter((mesh) => mesh.visible);
  }

  _pick(event) {
    return this.pickHits(this._ndc(event))[0] || null;
  }

  _handlePointerDown(event) {
    this._downPos = { x: event.clientX, y: event.clientY };
    if (event.button !== 0) return;
    // Section gizmo handles capture the drag (camera orbit stays off).
    if (this._sectionGizmo && this.mode === "orbit") {
      const gizmoHit = this._gizmoHandleAt(event);
      if (gizmoHit) {
        this._sectionDrag = {
          kind: gizmoHit.object.userData.handle,
          startPoint: this._section.point.clone(),
          startNormal: this._section.normal.clone(),
          startU: this._section.u.clone(),
          startV: this._section.v.clone(),
          startHit: gizmoHit.point.clone(),
        };
        this.controls.enabled = false;
        return;
      }
    }
    // Measurement points land on pointer-up: orbit drags stay available and
    // measuring never triggers component selection.
    if (this.mode === "measure") return;
    const hit = this._pick(event);
    if (this.mode !== "orbit") return;
    const node = hit ? hit.object.userData.node : null;
    this.highlight(node);
    this.onSelect?.(hit ? { node, layer: hit.object.userData.layer } : null);
  }

  resize() {
    const width = this.container.clientWidth || 1;
    const height = this.container.clientHeight || 1;
    // updateStyle must stay enabled — otherwise the canvas keeps its attribute
    // size in CSS pixels and overflows/overlaps the side panels.
    this.renderer.setSize(width, height, true);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.measures?.setResolution(width, height);
    this.viewCube?.layout();
    this._markDirty();
  }

  // Renders ONLY when something changed (camera, scene, materials) — the GPU
  // and CPU stay completely idle when the user is not interacting.
  _animate() {
    this._frame = requestAnimationFrame(() => this._animate());
    if (this._camTween) {
      const { from, to, start, duration } = this._camTween;
      const t = Math.min((performance.now() - start) / duration, 1);
      const eased = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
      this.camera.position.lerpVectors(from, to, eased);
      if (t >= 1) this._camTween = null;
      this._dirty = true;
    }
    // update() is cheap and only emits "change" (marking dirty) while moving.
    this.controls.update();
    // Section gizmo handles keep a constant screen size at any zoom level.
    if (this._sectionGizmo) {
      const handles = this._sectionGizmo.userData.handles;
      const dist = this.camera.position.distanceTo(this._sectionGizmo.position);
      const radius = this.bbox.getSize(new THREE.Vector3()).length() / 2 || 1;
      handles.scale.setScalar(Math.min(Math.max(dist * 0.12, radius * 0.35), radius * 1.4));
    }
    if (this._dirty) {
      this._dirty = false;
      this.renderer.render(this.scene, this.camera);
      this.viewCube?.update();
    }
  }

  dispose() {
    window.removeEventListener("resize", this._boundResize);
    window.removeEventListener("keydown", this._onKeyDown);
    this._resizeObserver?.disconnect();
    this.renderer.domElement.removeEventListener("pointerdown", this._onPointerDown);
    this.renderer.domElement.removeEventListener("pointermove", this._onPointerMove);
    window.removeEventListener("pointerup", this._onPointerUp);
    this.measures?.dispose();
    cancelAnimationFrame(this._frame);
    this.viewCube?.dispose();
    this.meshes.forEach((mesh) => mesh.material.dispose());
    this.renderer.dispose();
    while (this.container.firstChild) this.container.removeChild(this.container.firstChild);
  }
}
